// Utils for tracking user actions and sending data to the webhook

// Estender a interface Window para incluir nossas propriedades personalizadas
declare global {
    interface Window {
        fbq: any;
        fbPixelViewSent?: boolean;
    }
}

// Função simplificada para obter cookies e parâmetros importantes
export const getTrackingData = () => {
    const cookies = document.cookie.split(';');
    let fbp = '';
    let fbc = '';

    // Obter cookies do Facebook
    cookies.forEach(cookie => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith('_fbp=')) {
            fbp = trimmedCookie.substring(5);
        } else if (trimmedCookie.startsWith('_fbc=')) {
            fbc = trimmedCookie.substring(5);
        }
    });

    // Obter parâmetros da URL
    const params: Record<string, string> = {};
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    // Parâmetros importantes para rastreamento
    ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign'].forEach(param => {
        const value = urlParams.get(param);
        if (value) params[param] = value;
    });

    // Obter ou criar ID do usuário
    let userId = localStorage.getItem('bt_user_id');
    if (!userId) {
        // UUID v4 simplificado
        userId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
        localStorage.setItem('bt_user_id', userId);
    }

    return {
        fbp,
        fbc,
        userId,
        urlParams: params,
        landingPage: sessionStorage.getItem('landing_page') || window.location.href
    };
};

// Função unificada para capturar fbclid e inicializar tracking
export const initTracking = () => {
    try {
        // Capturar fbclid se presente
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');

        if (fbclid) {
            // Armazenar o fbclid em cookie e localStorage
            const fbc = `fb.1.${Date.now()}.${fbclid}`;
            document.cookie = `_fbc=${fbc}; max-age=${60 * 60 * 24 * 7}; path=/; SameSite=Lax`;
            localStorage.setItem('bt_fbc', fbc);
        }

        // Verificar/criar o cookie _fbp se não existir
        if (!document.cookie.includes('_fbp=')) {
            const newFbp = `fb.1.${Date.now()}.${Math.random().toString().substring(2)}`;
            document.cookie = `_fbp=${newFbp}; max-age=${60 * 60 * 24 * 7}; path=/; SameSite=Lax`;
            localStorage.setItem('bt_fbp', newFbp);
        }

        // Salvar página de entrada na primeira visita da sessão
        if (!sessionStorage.getItem('landing_page')) {
            sessionStorage.setItem('landing_page', window.location.href);
        }
    } catch (error) {
        // Falha silenciosa
    }
};

// Função simplificada para enviar eventos
export const sendEvent = (eventName: string, eventData: Record<string, any>) => {
    try {
        // Verificação especial para PageView - usar variável para controle
        if (eventName === 'PageView') {
            if (window.fbPixelViewSent) {
                console.log('Evento PageView já foi enviado nesta sessão, ignorando...');
                return;
            }
            // Marcar que o PageView foi enviado
            window.fbPixelViewSent = true;
        }

        // Prevenir envios duplicados recentes do mesmo evento
        const recentEvents = JSON.parse(sessionStorage.getItem('bt_recent_events') || '[]');
        const now = Date.now();

        // Verificar duplicados nos últimos 5 segundos
        if (recentEvents.some((event: any) =>
            now - event.time < 5000 &&
            event.name === eventName)) {
            return;
        }

        // Registrar este evento
        recentEvents.push({ name: eventName, time: now });
        if (recentEvents.length > 10) recentEvents.shift();
        sessionStorage.setItem('bt_recent_events', JSON.stringify(recentEvents));

        // Obter dados de rastreamento
        const trackingInfo = getTrackingData();

        // Construir dados do evento
        const eventPayload = {
            event_name: eventName,
            event_time: Math.floor(now / 1000),
            user_id: trackingInfo.userId,
            fbp: trackingInfo.fbp,
            fbc: trackingInfo.fbc,
            ...trackingInfo.urlParams,
            ...eventData,
            user_agent: navigator.userAgent,
            page_url: window.location.href,
            referrer: document.referrer,
            landing_page: trackingInfo.landingPage
        };

        // Enviar via Facebook Pixel se disponível
        if (typeof window.fbq === 'function') {
            window.fbq('track', eventName, eventPayload);
        }

        // Enviar para o servidor de tracking
        const trackingUrl = new URL('https://serversidetrackerlpgateway.blacktrack.cloud/track/');
        Object.entries(eventPayload).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                trackingUrl.searchParams.append(key, String(value));
            }
        });

        // Fazer a requisição - usando método único (fetch com fallback para beacon)
        fetch(trackingUrl.toString(), {
            method: 'GET',
            mode: 'no-cors',
            cache: 'no-cache',
            keepalive: true
        }).catch(() => {
            // Usar sendBeacon como fallback
            if (navigator.sendBeacon) {
                navigator.sendBeacon(trackingUrl.toString());
            }
        });

        return true;
    } catch (error) {
        // Falha silenciosa
        return false;
    }
};

// Alias específico para seleção de gateway
export const trackGatewaySelection = (gatewayName: string, gatewayUrl: string, data: any) => {
    return sendEvent('gateway_selection', {
        gateway_name: gatewayName,
        gateway_url: gatewayUrl,
        ...data
    });
};

// Para compatibilidade com o código existente
export const sendWebhookData = sendEvent;
export const captureFbclid = initTracking;
export const retryPendingWebhooks = () => { }; // Função vazia para manter compatibilidade
export const getFacebookCookies = () => {
    const { fbp, fbc } = getTrackingData();
    return { fbp, fbc };
}; 